import { UpperCasePipe } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	CollapseComponent,
	Icons,
	InfoGroupComponent,
	ItemComponent,
	LinkComponent,
	TagComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import {
	IConnectionOperation,
	IDocumentsDocs,
	IDocumentsOperation,
	IOperation,
	OPERATION_TYPE,
} from '../../../../../operations/models/operations.models';
import { LanguageService } from '../../../../../core/services/language.service';
import { MasterService } from '../../../../../shared/services/master.service';
import { resolveWorkflowLocaleText } from '../../../../utils/workflow-locale-text.util';
import { ModalDocDetailsComponent } from '../modal-doc-details/modal-doc-details.component';
import { DeveloperCurlButtonComponent } from '../../../../../shared/components/developer-curl-button/developer-curl-button.component';

@Component({
	selector: 'app-docs-operation',
	imports: [
		TranslatePipe,
		UpperCasePipe,
		TileComponent,
		CollapseComponent,
		ItemComponent,
		TagComponent,
		LinkComponent,
		InfoGroupComponent,
		ModalDocDetailsComponent,
		DeveloperCurlButtonComponent,
	],
	templateUrl: './docs-operation.component.html',
	styleUrl: './docs-operation.component.scss',
})
export class DocsOperationComponent implements OnInit {
	@Input() operation?: IDocumentsOperation;
	@Input() operations?: Array<IOperation>;
	@Input() index: number = 1;
	@Input() profileUuid?: string;

	public masterService = inject(MasterService);
	private translate = inject(TranslateService);
	private languageService = inject(LanguageService);

	public selectedDoc?: IDocumentsDocs;
	public showModalDocDetails: boolean = false;

	ngOnInit(): void {
		const connectionLikeDocument = this.operations.filter(
			(op: IOperation) => op.type === OPERATION_TYPE.CONNECTIONS && !op.config.evaluation,
		);
		if (connectionLikeDocument?.length) {
			if (!this.operation.config.docs) {
				this.operation.config.docs = [];
				this.operation.config.tutorial = true;
			}
			connectionLikeDocument.forEach((op: IConnectionOperation) => {
				this.operation.config.docs.push({
					uuid: op.uuid,
					document_type_uuid: '',
					type: op.config.type,
					required: true,
					method: undefined,
					docInfo: {
						title: '',
						description: '',
						type: '',
						icon: this.getDocumentIcon(op.config.type),
						uuid: '',
						confRecommended: {
							total_tries: op.config.total_tries,
							min: 0,
							max: 0,
							preview_validation: true,
							match_data: true,
							connection_available: true,
							connection_type: op.config.type,
							connection_uuid: op.uuid,
						},
					},
					icon: this.getDocumentIcon(op.config.type),
				});
			});
		}
	}

	public operationCurlPath(): string {
		if (!this.profileUuid || !this.operation?.uuid) return '';
		return `/profile/${this.profileUuid}/operations/${this.operation.uuid}`;
	}

	/**
	 * Obtiene el icono correspondiente para un tipo de documento específico.
	 *
	 * @param type - El tipo de documento para el cual se desea obtener el icono. Debe ser una cadena que represente el tipo, como 'DRIVE_LICENSE', 'DNI', etc.
	 * @returns El nombre del icono correspondiente al tipo de documento, o null si el tipo no está definido en el mapa de documentos.
	 */
	public getDocumentIcon(type: string): Icons | null {
		const docs = {
			DRIVE_LICENSE: 'icon-card',
			DNI: 'icon-card',
			PASSPORT: 'icon-card',
			NIE: 'icon-card',
			CERT_REG_CIU_UE: 'icon-card',
			IRPF: 'icon-euro',
			CHECK: 'icon-euro',
			INVOICE: 'icon-euro',
			WORKLIFE: 'icon-briefcase',
			PAYROLL: 'icon-briefcase',
			SCRIPTURE: 'icon-briefcase',
			GENERIC: 'icon-book-open',
		};

		return docs[type] ?? null;
	}

	/**
	 * Obtiene las claves (UUIDs) de un objeto de documentos.
	 *
	 * @param docRecord - El objeto que contiene documentos con UUIDs como claves.
	 * @returns Un array con las claves del objeto.
	 */
	public getDocumentKeys(docRecord: Record<string, any>): string[] {
		return Object.keys(docRecord || {});
	}

	public resolveDocTitle(doc: IDocumentsDocs | null | undefined): string {
		if (!doc) {
			return '';
		}
		const title = resolveWorkflowLocaleText(doc.title as any, this.languageService.getLanguage(), 'es');
		if (title.trim().length > 0) {
			return title;
		}
		return this.translate.instant(`DOCS.${doc.type}`);
	}

	public seeDetails(doc: IDocumentsDocs): void {
		this.selectedDoc = doc;
		this.showModalDocDetails = true;
	}

	public closeModal(): void {
		this.selectedDoc = undefined;
		this.showModalDocDetails = false;
	}
}
